import React, { useEffect, useMemo, useState } from 'react';
import { childrenAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLOR = { online: '#22c55e', lowbatt: '#e63946', offline: 'rgba(255,255,255,.72)', all: 'rgba(255,255,255,.72)' };
const STATUS_LABEL = { online: 'EN LIGNE', lowbatt: 'BATT. FAIBLE', offline: 'HORS LIGNE' };

function BatteryBar({ pct }) {
  if (pct === null || pct === undefined) return <span style={{ color: 'rgba(255,255,255,.72)', fontSize: 11 }}>-</span>;
  const safePct = Math.max(0, Math.min(100, Number(pct) || 0));
  const c = safePct < 20 ? '#e63946' : safePct < 40 ? '#f5a623' : '#22c55e';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 30, height: 13, border: `1px solid ${c}`, borderRadius: 3, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <div style={{ width: `${safePct}%`, maxWidth: '90%', height: 9, background: c, borderRadius: 2, margin: '0 1px' }}/>
        <div style={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 3, height: 7, background: c, borderRadius: 1 }}/>
      </div>
      <span style={{ fontSize: 11, color: c, fontWeight: 800 }}>{safePct}%</span>
    </div>
  );
}

function initials(name = '') {
  return String(name || 'E').split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase() || 'E';
}

function ageText(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'à l\'instant';
  if (diff < 3_600_000) return `il y a ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `il y a ${Math.floor(diff / 3_600_000)} h`;
  return date.toLocaleString('fr-FR');
}

function formatPlace(location) {
  const label = location?.placeLabel || location?.address || location?.displayName;
  if (label) return label;
  if (location?.latitude && location?.longitude) return 'Position disponible';
  return 'Aucune position';
}

function formatAccuracy(location) {
  const accuracy = Number(location?.accuracy);
  if (!Number.isFinite(accuracy) || accuracy <= 0) return '-';
  return accuracy < 1000 ? Math.round(accuracy) + ' m' : (accuracy / 1000).toFixed(1) + ' km';
}

function positionQualityLabel(location) {
  if (location?.positionQuality === 'GPS_PRECISE') return 'GPS précis';
  if (location?.positionQuality === 'APPROXIMATE') return 'Position approximative';
  if (location?.positionQuality === 'UNAVAILABLE') return 'Localisation indisponible';
  return location?.latitude != null && location?.longitude != null ? 'Qualité inconnue' : 'Localisation indisponible';
}

function positionSourceLabel(source) {
  if (source === 'device_gps') return 'GPS du traceur';
  if (source === 'device_reported') return 'Coordonnées fournies par le traceur';
  if (source === 'unavailable') return 'Indisponible';
  return source || '-';
}

function technicalValue(value) {
  return value === null || value === undefined || value === '' ? '-' : String(value);
}

function normalizeChild(child) {
  const battery = child.batteryLevel ?? child.battery ?? child.lastLocation?.batteryLevel ?? null;
  const status = child.isOnline ? 'online' : Number(battery) < 20 ? 'lowbatt' : 'offline';
  return { ...child, battery, status };
}

export function canManageChildLinks(user) {
  return Array.isArray(user?.permissions) && user.permissions.includes('children:manage');
}

export default function ChildrenPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [unlinkingKey, setUnlinkingKey] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const canManageLinks = canManageChildLinks(user);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await childrenAPI.getChildren(1, 80, search);
        const rows = (response.data?.children || []).map(normalizeChild);
        if (!cancelled) {
          setChildren(rows);
          setSelected(current => current ? rows.find(c => c.id === current.id) || null : null);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Lecture des enfants impossible.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(load, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, refreshKey]);

  const filtered = useMemo(() => children.filter(c => filter === 'all' || c.status === filter), [children, filter]);
  const stats = useMemo(() => ({
    all: children.length,
    online: children.filter(c => c.status === 'online').length,
    lowbatt: children.filter(c => c.status === 'lowbatt').length,
    offline: children.filter(c => c.status === 'offline').length,
  }), [children]);

  const handleUnlinkParent = async (parentId, label) => {
    if (!selected?.id || !parentId) return;
    const ok = window.confirm(`Dissocier ${label} de ${selected.name} ?\n\nSeule cette relation de co-parent sera désactivée. Le profil, son parent principal, ses traceurs et son historique seront conservés.`);
    if (!ok) return;
    const key = `${selected.id}:${parentId}`;
    setUnlinkingKey(key);
    setActionMessage('');
    setActionError('');
    try {
      const response = await childrenAPI.unlinkParent(selected.id, parentId);
      const mode = response.data?.mode === 'primary' ? 'parent principal' : 'co-parent';
      setActionMessage(`Dissociation du ${mode} effectuee.`);
      setRefreshKey(k => k + 1);
      setSelected(null);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Dissociation impossible.');
    } finally {
      setUnlinkingKey('');
    }
  };

  return (
    <div style={S.page}>
      <div style={S.pillRow}>
        {[
          ['all', 'Tous', stats.all, STATUS_COLOR.all],
          ['online', 'En ligne', stats.online, STATUS_COLOR.online],
          ['lowbatt', 'Batt. faible', stats.lowbatt, STATUS_COLOR.lowbatt],
          ['offline', 'Hors ligne', stats.offline, STATUS_COLOR.offline],
        ].map(([k, label, count, color]) => (
          <button key={k} style={{ ...S.pill, ...(filter === k ? { borderColor: color, color, background: color + '18' } : {}) }} onClick={() => setFilter(k)}>
            {label} <span style={{ ...S.pillCount, color }}>{count}</span>
          </button>
        ))}
        <input style={S.search} placeholder="Nom, parent, téléphone..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error && <div style={S.error}>{error}</div>}
      {actionMessage && <div style={S.success}>{actionMessage}</div>}
      {actionError && <div style={S.error}>{actionError}</div>}
      {loading && <div style={S.empty}>Chargement des vraies données PostgreSQL...</div>}

      <div style={S.layout}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.tableCard}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['ENFANT', 'PARENT', 'TÉLÉPHONE', 'POSITION', 'BATTERIE', 'STATUT', 'VU'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} style={{ ...S.tr, background: selected?.id === c.id ? 'rgba(255,255,255,.06)' : 'transparent' }} onClick={() => setSelected(s => s?.id === c.id ? null : c)}>
                    <td style={S.td}><div style={S.nameCell}><div style={S.avatar}>{initials(c.name)}</div><div><div style={S.name}>{c.name}</div><div style={S.sub}>{c.school || 'École non renseignée'}</div></div></div></td>
                    <td style={S.td}><div style={S.name}>{c.parentName || '-'}</div><div style={S.sub}>{c.parentPhone || '-'}</div></td>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#fff', fontSize: 11 }}>{c.phone || '-'}</td>
                    <td style={{ ...S.td, color: '#fff', fontSize: 11, maxWidth: 240, whiteSpace: 'normal', lineHeight: 1.35 }}>{formatPlace(c.lastLocation)}</td>
                    <td style={S.td}><BatteryBar pct={c.battery} /></td>
                    <td style={S.td}><span style={{ ...S.tag, background: STATUS_COLOR[c.status] + '22', color: STATUS_COLOR[c.status] }}>{STATUS_LABEL[c.status]}</span></td>
                    <td style={{ ...S.td, color: '#fff', fontSize: 11 }}>{ageText(c.lastSeenAt || c.updatedAt)}</td>
                  </tr>
                ))}
                {!loading && !filtered.length && <tr><td style={S.emptyCell} colSpan="7">Aucun enfant trouvé dans la DB pour ce filtre.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {selected && (
          <div style={S.detail}>
            <div style={S.detailTop}>
              <div style={S.bigAvatar}>{initials(selected.name)}</div>
              <div style={{ flex: 1 }}><div style={S.detailName}>{selected.name}</div><div style={S.sub}>{selected.school || 'École non renseignée'}</div></div>
              <button style={S.closeBtn} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={{ padding: 14 }}>
              {(() => {
                const location = selected.location || selected.lastLocation;
                return [
                  ['Parent principal', selected.parentName || '-'],
                  ['Téléphone parent', selected.parentPhone || '-'],
                  ['Téléphone enfant', selected.phone || '-'],
                  ['Association', selected.linkStatus || '-'],
                  ['Batterie', selected.battery == null ? '-' : `${selected.battery}%`],
                  ['Dernière lecture', ageText(selected.lastSeenAt || selected.updatedAt)],
                  ['Quartier / Ville', formatPlace(selected.lastLocation)],
                  ['Latitude', technicalValue(location?.latitude)],
                  ['Longitude', technicalValue(location?.longitude)],
                  ['Qualité de position', positionQualityLabel(location)],
                  ['Source', positionSourceLabel(location?.positionSource)],
                  ['Satellites', technicalValue(location?.satellites)],
                  ['Précision déclarée', formatAccuracy(selected.lastLocation)],
                ].map(([l, v]) => <div key={l} style={S.field}><span style={S.fieldLabel}>{l}</span><span style={S.fieldVal}>{v}</span></div>);
              })()}
              {selected.tracker && (
                <>
                  <div style={S.sectionTitle}>RÉSEAU DU TRACEUR</div>
                  {[
                    ['MCC', technicalValue(selected.cellular?.mcc)],
                    ['MNC', technicalValue(selected.cellular?.mnc)],
                    ['LAC/TAC', technicalValue(selected.cellular?.lac ?? selected.cellular?.tac)],
                    ['Cell ID', technicalValue(selected.cellular?.cellId)],
                    ['Réseau', technicalValue(selected.cellular?.radioTechnology)],
                    ['Signal GSM', technicalValue(selected.cellular?.signal)],
                    ['Protocole', technicalValue(selected.tracker.protocol)],
                    ['Device ID', technicalValue(selected.tracker.deviceId)],
                    ['Dernière mise à jour réseau', ageText(selected.cellular?.updatedAt || selected.tracker.lastCommunication)],
                  ].map(([l, v]) => <div key={l} style={S.field}><span style={S.fieldLabel}>{l}</span><span style={S.fieldVal}>{v}</span></div>)}
                </>
              )}
              {canManageLinks && (
                <><div style={S.sectionTitle}>ACTIONS ADMINISTRATIVES</div>
                  <button style={S.deleteBtn} disabled title="Le parent principal doit d’abord être transféré.">Dissocier le parent principal</button>
                  <button style={S.deleteBtn} disabled title="Une évolution du modèle parent/enfant est requise.">Retirer du suivi</button>
                  <div style={S.sub}>Ces actions sont indisponibles tant que le modèle conserve un parent principal obligatoire. Les co-parents peuvent être dissociés individuellement ci-dessous.</div>
                </>
              )}
              {selected.lastLocation?.mapsLink && <a style={S.mapLink} href={selected.lastLocation.mapsLink} target="_blank" rel="noreferrer">Ouvrir dans Google Maps</a>}
              <div style={S.sectionTitle}>CO-PARENTS</div>
              {(selected.coparents || []).map(cp => (
                <div key={`${cp.parentId}-${cp.phone}`} style={S.histRow}>
                  <div style={S.dot}/>
                  <div style={{ flex: 1 }}>
                    <div style={S.name}>{cp.name || cp.phone}</div>
                    <div style={S.sub}>{cp.role} - {cp.status}</div>
                  </div>
                  {canManageLinks && cp.parentId && cp.status !== 'disabled' && (
                    <button
                      style={S.dangerSmall}
                      disabled={unlinkingKey === `${selected.id}:${cp.parentId}`}
                      onClick={() => handleUnlinkParent(cp.parentId, cp.name || cp.phone || 'ce co-parent')}
                    >
                      {unlinkingKey === `${selected.id}:${cp.parentId}` ? '...' : 'Dissocier'}
                    </button>
                  )}
                </div>
              ))}
              {!(selected.coparents || []).length && <div style={S.sub}>Aucun co-parent associé.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20 },
  pillRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  pill: { background: '#10224a', border: '1px solid rgba(255,255,255,.18)', color: '#fff', fontSize: 12, padding: '7px 13px', borderRadius: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  pillCount: { fontWeight: 900 },
  search: { marginLeft: 'auto', background: '#10224a', border: '1px solid rgba(255,255,255,.18)', color: '#fff', fontSize: 13, padding: '8px 12px', borderRadius: 8, outline: 'none', width: 280 },
  layout: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  tableCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, overflow: 'auto' },
  th: { fontSize: 10, color: '#fff', textTransform: 'uppercase', letterSpacing: .8, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.16)', textAlign: 'left', fontWeight: 800, whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,.08)', verticalAlign: 'middle' },
  tr: { cursor: 'pointer' },
  nameCell: { display: 'flex', alignItems: 'center', gap: 10 },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: '#e6b32522', border: '1px solid #e6b32566', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5c542', fontWeight: 900, fontSize: 11 },
  name: { fontSize: 12, color: '#fff', fontWeight: 800 },
  sub: { fontSize: 11, color: 'rgba(255,255,255,.78)', marginTop: 2 },
  tag: { padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' },
  detail: { width: 310, background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, flexShrink: 0, overflow: 'hidden' },
  detailTop: { display: 'flex', alignItems: 'center', gap: 10, padding: 14, borderBottom: '1px solid rgba(255,255,255,.16)' },
  bigAvatar: { width: 42, height: 42, borderRadius: '50%', background: '#e6b32522', border: '1px solid #e6b32566', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5c542', fontWeight: 900 },
  detailName: { fontSize: 15, fontWeight: 900, color: '#fff' },
  closeBtn: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, marginLeft: 'auto' },
  field: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.08)' },
  fieldLabel: { fontSize: 11, color: 'rgba(255,255,255,.72)' },
  fieldVal: { fontSize: 11, color: '#fff', textAlign: 'right', fontWeight: 700, maxWidth: 160 },
  sectionTitle: { fontSize: 10, color: '#fff', letterSpacing: 1, textTransform: 'uppercase', marginTop: 14, marginBottom: 8, fontWeight: 900 },
  histRow: { display: 'flex', gap: 10, marginBottom: 10 },
  dot: { width: 7, height: 7, borderRadius: '50%', background: '#22c55e', flexShrink: 0, marginTop: 4 },
  mapLink: { display: 'block', marginTop: 12, color: '#f5c542', fontWeight: 800, textDecoration: 'none' },
  error: { marginBottom: 14, background: 'rgba(230,57,70,.18)', border: '1px solid rgba(230,57,70,.45)', color: '#fff', padding: 12, borderRadius: 8 },
  success: { marginBottom: 14, background: 'rgba(34,197,94,.16)', border: '1px solid rgba(34,197,94,.42)', color: '#fff', padding: 12, borderRadius: 8 },
  dangerBtn: { width: '100%', marginTop: 12, background: 'rgba(230,57,70,.14)', border: '1px solid rgba(230,57,70,.5)', color: '#ffb4b4', fontFamily: 'monospace', fontSize: 11, fontWeight: 900, padding: '9px 10px', borderRadius: 6, cursor: 'pointer' },
  deleteBtn: { width: '100%', marginTop: 10, background: 'rgba(230,57,70,.28)', border: '1px solid rgba(230,57,70,.78)', color: '#fff', fontFamily: 'monospace', fontSize: 11, fontWeight: 900, padding: '10px 10px', borderRadius: 6, cursor: 'pointer' },
  dangerSmall: { background: 'rgba(230,57,70,.12)', border: '1px solid rgba(230,57,70,.45)', color: '#ffb4b4', fontFamily: 'monospace', fontSize: 10, fontWeight: 900, padding: '4px 7px', borderRadius: 5, cursor: 'pointer', alignSelf: 'center' },
  empty: { marginBottom: 14, color: '#fff', background: '#10224a', border: '1px solid rgba(255,255,255,.16)', padding: 12, borderRadius: 8 },
  emptyCell: { padding: 24, color: '#fff', textAlign: 'center' },
};
